/**
 * P3 damage callouts — pure helpers.
 *
 * Extracts the math + label formatting used by `<DamageCallouts>` so it
 * unit-tests without dragging in R3F. The component owns the active
 * list and lifecycle; this module just shapes the inputs/outputs.
 */

import type { RenderEvent } from "../replay/events";

export const CALLOUT_DURATION_MS = 900;
export const CALLOUT_FLOAT_DISTANCE = 0.8;
export const CALLOUT_FONT_SIZE = 0.32;
export const CALLOUT_CRIT_THRESHOLD = 10;
export const CALLOUT_OUTLINE_WIDTH = 0.007;

export interface ActiveCallout {
  /** Stable key — same hit at the same tick shouldn't spawn twice. */
  id: string;
  /** Tick the hit landed at. */
  tick: number;
  /** Which side took the damage; the renderer uses this for color. */
  victim: "A" | "B";
  /** Pre-formatted label, e.g. "−7 LARM" or "⚡−15 TORSO". */
  label: string;
  /** World-space anchor (set when the callout spawned). */
  origin: [number, number, number];
  /** `performance.now()` at spawn. Drives float + fade. */
  startedAt: number;
}

/**
 * Format a hit event's damage + zone into a compact callout string.
 * Prepends a ⚡ glyph for heavy hits (>= `CALLOUT_CRIT_THRESHOLD`) so
 * the reader can spot crits without parsing numbers.
 */
export function formatCalloutLabel(damage: number, zone: string): string {
  const z = zone.toUpperCase();
  const lead = damage >= CALLOUT_CRIT_THRESHOLD ? "⚡" : "";
  return `${lead}−${damage} ${z}`;
}

/**
 * Build the stable id used to dedupe a hit-event-to-callout mapping
 * within a single ReplayScene mount. The same (tick, victim, zone,
 * index) shouldn't spawn twice on a re-render. Index lets multiple
 * hits at the same tick coexist (e.g. multi-shot weapons).
 */
export function calloutId(tick: number, victim: "A" | "B", zone: string, index: number): string {
  return `hit-${tick}-${victim}-${zone}-${index}`;
}

/**
 * Linearly interpolate callout opacity (fades in slightly faster than
 * it fades out so the label "pops" then drifts).
 */
export function calloutOpacity(elapsedMs: number): number {
  if (elapsedMs <= 0) return 0;
  if (elapsedMs >= CALLOUT_DURATION_MS) return 0;
  const t = elapsedMs / CALLOUT_DURATION_MS;
  // Quick ease-in over first 10%, linear fade-out over remainder.
  if (t < 0.1) return t / 0.1;
  return 1 - (t - 0.1) / 0.9;
}

/**
 * Y-axis float offset for a callout that's been alive for `elapsedMs`.
 * Linear ramp from 0 → `CALLOUT_FLOAT_DISTANCE`.
 */
export function calloutFloat(elapsedMs: number): number {
  const t = Math.max(0, Math.min(1, elapsedMs / CALLOUT_DURATION_MS));
  return t * CALLOUT_FLOAT_DISTANCE;
}

/**
 * Convenience: is this event a hit we want to render? Lets the
 * component dedupe its filter.
 */
export function isHitEvent(e: RenderEvent): e is Extract<RenderEvent, { kind: "hit" }> {
  return e.kind === "hit";
}
